/**
 * AiUsageTab — AI tools > Usage admin view (Phase X.3.7 Slice B).
 *
 * Three read-only views over ai_invocations, owner/admin only, tenant-scoped:
 *   1. Who x what grid    — user x tool invocation counts over the window.
 *   2. Token spend (rough)— per-tool token totals + a labeled cost estimate.
 *   3. Activity feed      — recent invocations: who, tool, when, status, tokens.
 * A time filter (7 / 30 / 90 days) drives all three.
 *
 * The server aggregates in SQL (GROUP BY / LIMIT) and never returns the PHI
 * payload columns; the "for whom" subject drill-in is deferred to phase 2.
 *
 * Tools are keyed by friendly label (labelForRoute), so several raw routes that
 * map to one tool collapse into a single column. Every displayed number is
 * rounded for display (counts/tokens are integers; the cost estimate to cents).
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  aiUsageApi,
  estimateCostUsd,
  labelForRoute,
  WINDOW_OPTIONS,
  type RecentInvocation,
  type UsageGridCell,
  type UsageReport,
  type WindowDays,
} from '@/lib/aiUsage';
import { formatRecordCount, formatRelative } from '@/lib/format';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';

export interface AiUsageTabProps {
  tenantSlug: string;
}

interface ToolColumn {
  label: string;
  total: number;
}

interface UserRow {
  user_id: string;
  display: string;
  counts: Map<string, number>; // tool label -> invocation count
  total: number;
}

interface ToolTokenTotal {
  label: string;
  tokens: number;
}

const USD = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** Best display name for a user row: name, else email, else short id. */
function personLabel(
  display: string | null,
  email: string | null,
  userId: string,
): string {
  if (display && display.trim().length > 0) return display.trim();
  if (email && email.trim().length > 0) return email.trim();
  return `${userId.slice(0, 8)}…`;
}

/** Pivot the flat grid cells into tool columns + per-user rows, keyed by label. */
function pivotGrid(grid: UsageGridCell[]): { columns: ToolColumn[]; rows: UserRow[] } {
  const colTotals = new Map<string, number>();
  const userMap = new Map<string, UserRow>();

  for (const cell of grid) {
    const label = labelForRoute(cell.route);
    colTotals.set(label, (colTotals.get(label) ?? 0) + cell.invocations);

    let row = userMap.get(cell.user_id);
    if (!row) {
      row = {
        user_id: cell.user_id,
        display: personLabel(cell.user_display_name, cell.user_email, cell.user_id),
        counts: new Map(),
        total: 0,
      };
      userMap.set(cell.user_id, row);
    }
    row.counts.set(label, (row.counts.get(label) ?? 0) + cell.invocations);
    row.total += cell.invocations;
  }

  const columns = [...colTotals.entries()]
    .map(([label, total]) => ({ label, total }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  const rows = [...userMap.values()].sort(
    (a, b) => b.total - a.total || a.display.localeCompare(b.display),
  );
  return { columns, rows };
}

/** Per-tool token totals (input + output) + grand total, keyed by label. */
function tokenTotals(grid: UsageGridCell[]): { tools: ToolTokenTotal[]; grandTotal: number } {
  const byLabel = new Map<string, number>();
  let grandTotal = 0;
  for (const cell of grid) {
    const label = labelForRoute(cell.route);
    const tokens = cell.input_tokens + cell.output_tokens;
    byLabel.set(label, (byLabel.get(label) ?? 0) + tokens);
    grandTotal += tokens;
  }
  const tools = [...byLabel.entries()]
    .map(([label, tokens]) => ({ label, tokens }))
    .sort((a, b) => b.tokens - a.tokens || a.label.localeCompare(b.label));
  return { tools, grandTotal };
}

export function AiUsageTab({ tenantSlug }: AiUsageTabProps): ReactNode {
  const [windowDays, setWindowDays] = useState<WindowDays>(30);

  const query = useQuery<UsageReport, Error>({
    queryKey: ['admin-review', 'ai-usage', tenantSlug, windowDays],
    queryFn: ({ signal }) => aiUsageApi.report(tenantSlug, windowDays, signal),
    staleTime: 30_000,
  });

  const grid = query.data?.grid ?? [];
  const feed = query.data?.feed ?? [];

  const { columns, rows } = useMemo(() => pivotGrid(grid), [grid]);
  const { tools, grandTotal } = useMemo(() => tokenTotals(grid), [grid]);

  return (
    <div>
      {/* Time filter */}
      <div className="flex gap-2 mb-4 items-center">
        <span className="text-xs text-ink-soft">Window:</span>
        {WINDOW_OPTIONS.map((option) => {
          const isActive = windowDays === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setWindowDays(option)}
              aria-pressed={isActive}
              className={
                'min-h-9 px-3 py-1 text-xs font-medium rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (isActive
                  ? 'bg-sage-100 text-sage-700 border border-sage-300'
                  : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30')
              }
            >
              {`${option} days`}
            </button>
          );
        })}
      </div>

      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="AI usage" />}

      {query.isSuccess && grid.length === 0 && feed.length === 0 && (
        <div className="px-3 py-8 text-center bg-sage-50 border border-sage-100 rounded-card text-sm text-ink">
          As the team uses the tools, you'll see usage here.
        </div>
      )}

      {query.isSuccess && (grid.length > 0 || feed.length > 0) && (
        <div className="flex flex-col gap-6">
          {/* Who x what grid */}
          {rows.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-2">Who used what</h3>
              <div className="overflow-x-auto bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                      <th className="px-3 py-2 font-medium">Person</th>
                      {columns.map((col) => (
                        <th key={col.label} className="px-3 py-2 font-medium text-right whitespace-nowrap">
                          {col.label}
                        </th>
                      ))}
                      <th className="px-3 py-2 font-medium text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.user_id} className="border-b border-sage-800/[5%] last:border-0">
                        <td className="px-3 py-2 text-ink">{row.display}</td>
                        {columns.map((col) => {
                          const n = row.counts.get(col.label) ?? 0;
                          return (
                            <td key={col.label} className="px-3 py-2 text-right tabular-nums text-ink-soft">
                              {n === 0 ? '–' : formatRecordCount(n)}
                            </td>
                          );
                        })}
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-ink">
                          {formatRecordCount(row.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Token spend (rough estimate) */}
          {tools.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-1">Token spend</h3>
              <p className="text-xs text-ink-soft mb-2">
                Order-of-magnitude estimate only. Totals are input + output tokens; the dollar
                figure applies one flat blended rate across every model, so heavier models can
                run several times higher. Not a billed amount.
              </p>
              <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-sage-800/[8%] text-left text-xs text-ink-soft">
                      <th className="px-3 py-2 font-medium">Tool</th>
                      <th className="px-3 py-2 font-medium text-right">Tokens</th>
                      <th className="px-3 py-2 font-medium text-right">Est. cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tools.map((tool) => (
                      <tr key={tool.label} className="border-b border-sage-800/[5%]">
                        <td className="px-3 py-2 text-ink">{tool.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-soft">
                          {formatRecordCount(tool.tokens)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-ink-soft">
                          {USD.format(estimateCostUsd(tool.tokens))}
                        </td>
                      </tr>
                    ))}
                    <tr className="font-medium text-ink">
                      <td className="px-3 py-2">All tools</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {formatRecordCount(grandTotal)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {USD.format(estimateCostUsd(grandTotal))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Activity feed */}
          {feed.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-ink mb-2">Recent activity</h3>
              <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
                {feed.map((row) => (
                  <ActivityRow key={row.id} row={row} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ row }: { row: RecentInvocation }): ReactNode {
  const tokens = (row.input_tokens ?? 0) + (row.output_tokens ?? 0);
  const isOk = row.status === 'ok' || row.status === 'success';
  const reason = isOk
    ? null
    : [row.error_code, row.error_detail].filter((s): s is string => typeof s === 'string' && s.length > 0).join(': ');
  return (
    <div className="flex items-center gap-3 px-3 py-2 border-b border-sage-800/[5%] last:border-0 text-sm">
      <div className="flex-1 min-w-0">
        <span className="text-ink">{labelForRoute(row.route)}</span>
        <span className="text-ink-soft"> by {personLabel(row.user_display_name, null, row.user_id)}</span>
        {reason !== null && reason.length > 0 && (
          <div className="text-xs text-ink-soft break-words" data-testid="ai-usage-error-reason">
            {reason}
          </div>
        )}
      </div>
      {!isOk && (
        <span className="text-xs px-2 py-0.5 rounded-pill bg-warm-50 border border-warm-300 text-ink whitespace-nowrap">
          {row.status}
        </span>
      )}
      <span className="text-xs text-ink-soft tabular-nums whitespace-nowrap">
        {formatRecordCount(tokens)} tok
      </span>
      <span className="text-xs text-ink-soft whitespace-nowrap">{formatRelative(row.occurred_at)}</span>
    </div>
  );
}
